/**
 * @file     PillRow.tsx
 * @readme   ../README.md
 * @purpose  One labelled pill group, single- or multi-select, shared by the address step (beds/baths) and the standalone 'pills' step (square footage, amenities). Multi-select stores its picks comma-joined so the flat answers map is unchanged.
 */
'use client';

import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { FlowIcon } from './flowIcons';
import { FlowChip } from '../components/FlowChip';
import { flowColors } from '../components/flowKitTokens';
import type { PillGroup } from '../types';

type PillRowProps = {
  group: PillGroup;
  /** Current answer value: a single value, or comma-joined for multi. */
  value: string;
  onChange: (value: string) => void;
  columns?: number;
};

export const splitMulti = (value: string): string[] =>
  value ? value.split(',').map((v) => v.trim()).filter(Boolean) : [];

export const PillRow = ({ group, value, onChange, columns = 4 }: PillRowProps) => {
  const { t } = useTranslation();
  const labelId = useId();
  const selectedSet = new Set(group.multi ? splitMulti(value) : value ? [value] : []);

  const toggle = (optValue: string) => {
    if (!group.multi) {
      onChange(optValue);
      return;
    }
    if (group.maxSelections) {
      // Capped groups keep selection order so the FIFO drop is stable: a pick
      // past the cap evicts the earliest choice instead of being refused.
      const ordered = splitMulti(value);
      const next = ordered.includes(optValue)
        ? ordered.filter((v) => v !== optValue)
        : [...ordered, optValue].slice(-group.maxSelections);
      onChange(next.join(','));
      return;
    }
    const next = new Set(selectedSet);
    if (next.has(optValue)) next.delete(optValue);
    else next.add(optValue);
    onChange(group.options.filter((o) => next.has(o.value)).map((o) => o.value).join(','));
  };

  // Icon options (home types) read better as an even grid; short labels
  // (bedroom counts) wrap as chips sized to their text.
  const hasOptionIcons = group.options.some((o) => o.icon);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography
        component="p"
        id={labelId}
        sx={{ m: 0, display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, fontWeight: 500, color: flowColors.textSecondary }}
      >
        {group.groupIcon ? (
          <Box component="span" aria-hidden="true" sx={{ color: flowColors.textMuted, display: 'inline-flex' }}>
            <FlowIcon name={group.groupIcon} size={18} />
          </Box>
        ) : null}
        {t(group.labelKey, { defaultValue: group.labelDefault })}
        {group.multi && group.maxSelections ? (
          <Box
            component="span"
            sx={{ ml: 'auto', fontSize: 13, fontWeight: 500, color: flowColors.link }}
          >
            {t('leadFlow.pills.chosenCount', {
              defaultValue: '{{count}} of {{max}} chosen',
              count: selectedSet.size,
              max: group.maxSelections,
            })}
          </Box>
        ) : null}
      </Typography>
      <Box
        role="group"
        aria-labelledby={labelId}
        sx={
          hasOptionIcons
            ? { display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: `repeat(${Math.min(columns, 3)}, minmax(0, 1fr))` }, gap: 2 }
            : { display: 'flex', flexWrap: 'wrap', gap: 2 }
        }
      >
        {group.options.map((opt) => (
          <FlowChip
            key={opt.value}
            selected={selectedSet.has(opt.value)}
            onClick={() => toggle(opt.value)}
            block={hasOptionIcons}
            icon={opt.icon ? <FlowIcon name={opt.icon} size={18} /> : undefined}
          >
            {t(opt.labelKey, { defaultValue: opt.labelDefault })}
          </FlowChip>
        ))}
      </Box>
    </Box>
  );
};
